import { Cart } from '@app/types/monolith/cart';
import {
  AddCartLineItemErrorCodes,
  AddLineItemAPIError,
} from '@app/types/monolith/types';
import { useUpdateCartItemsCount } from '@redbubble/next/cart-items-count';
import logger from '@redbubble/next/logger';
import { useRef, useState } from 'react';
import { CartCheckoutClient } from '../../cartCheckoutClient';
import { DispatchActionsType, StateDispatch } from '../types';

export type HandlerResult = {
  isAddingCartLineItem: boolean;
  cart?: Cart;
  errors?: AddLineItemAPIError[];
};

type AddGiftCardParams = {
  giftCardAmount: number;
};

type AddCartItemParams = {
  inventoryItemId: number | string;
  quantity: number;
};

export const useAddCartLineItem = ({
  client,
  dispatch,
}: {
  client: CartCheckoutClient;
  dispatch: StateDispatch;
}) => {
  const abortController = useRef<AbortController>();
  const updateCartItemsCount = useUpdateCartItemsCount();
  const [result, setResult] = useState<HandlerResult>({
    cart: undefined,
    isAddingCartLineItem: false,
    errors: undefined,
  });

  const executeRequest = async (
    requestFn: (
      signal: AbortSignal
    ) => Promise<{ cart: Cart } | { errors: AddLineItemAPIError[] }>
  ): Promise<HandlerResult> => {
    if (abortController.current) {
      abortController.current.abort();
    }
    const newAbortController = new AbortController();
    abortController.current = newAbortController;

    dispatch({ type: DispatchActionsType.SET_CART_LOADING, payload: true });
    setResult((prev) => ({ ...prev, isAddingCartLineItem: true }));

    return requestFn(newAbortController.signal)
      .then((resp) => {
        if ('cart' in resp) {
          dispatch({ type: DispatchActionsType.SET_CART, payload: resp.cart });
          updateCartItemsCount(resp.cart.total_items_count);
          return {
            cart: resp.cart,
            isAddingCartLineItem: false,
            errors: undefined,
          };
        }

        dispatch({
          type: DispatchActionsType.SET_CART_LOADING,
          payload: false,
        });

        return {
          cart: undefined,
          isAddingCartLineItem: false,
          errors: resp.errors,
        };
      })
      .catch((err) => {
        const errMessage = err instanceof Error ? err.message : String(err);
        const addCartLineItemError = `Unexpected error while adding cart line item using useAddCartLineItem() "${errMessage}"`;

        logger.error(
          {
            err: err,
            message: addCartLineItemError,
            context: 'CartAndDeliveryDatesDataContext/useAddCartLineItem',
          },
          addCartLineItemError
        );

        dispatch({
          type: DispatchActionsType.SET_CART_LOADING,
          payload: false,
        });

        return {
          cart: undefined,
          isAddingCartLineItem: false,
          errors: [
            {
              error_code:
                AddCartLineItemErrorCodes.CartItemCreateUnexpectedError,
            },
          ],
        };
      })
      .then((res) => {
        setResult(res);
        return res;
      });
  };

  const addCartItem = async ({
    inventoryItemId,
    quantity,
  }: AddCartItemParams): Promise<HandlerResult> => {
    return executeRequest((signal) =>
      client.addCartLineItem(
        {
          inventory_item_id: `${inventoryItemId}`,
          quantity,
        },
        signal
      )
    );
  };

  const addGiftCard = async ({
    giftCardAmount,
  }: AddGiftCardParams): Promise<HandlerResult> => {
    return executeRequest((signal) =>
      client.addGiftCard(
        {
          amount: giftCardAmount,
        },
        signal
      )
    );
  };

  return {
    ...result,
    abortController,
    addCartItem,
    addGiftCard,
  };
};
